import { useQuery } from '@tanstack/react-query';

import { fetchOrganizationFiles } from '@/api/v4/organization-files.api';
import { fetchSpotlightOrganizationFiles } from '@/api/v4/spotlight.api';
import { QueryKey } from '@/config/query-client';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { companyIdState } from '../../company.state';
import {
  type CompanyFetchMode,
  useCompanyFetchMode,
} from '../../company-fetch-context';
import { useAtomValue } from 'jotai';

const companyFilesFetchers: Record<
  CompanyFetchMode,
  (companyId: string) => ReturnType<typeof fetchOrganizationFiles>
> = {
  general: fetchOrganizationFiles,
  spotlight: fetchSpotlightOrganizationFiles,
};

export const useCompanyFiles = () => {
  const companyId = useAtomValue(companyIdState);
  const fetchMode = useCompanyFetchMode();

  const { hasRequiredPermission } = useUserPermissions();

  const fetchFiles = async () => {
    if (!companyId) return [];

    return companyFilesFetchers[fetchMode](companyId);
  };

  return useQuery({
    queryKey: [QueryKey.CompanyFiles, fetchMode, companyId],
    queryFn: fetchFiles,
    enabled: !!companyId && hasRequiredPermission(PERMISSION.VIEW_FILES),
    staleTime: 1000 * 60 * 5,
  });
};
